import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Circle, X } from "lucide-react";
import { getAdminTeams, getAdminUsers, getClubSettings } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";

/**
 * First-run setup checklist for a freshly-onboarded club admin.
 *
 * Rendered on `/admin` (Overview) for holders of `club_settings:manage`
 * — the club-settings query 403s for everyone else and the card simply
 * doesn't render. Driven entirely by data the admin surface already has:
 * the club's OWN settings rows (`own_keys` — inherited global fallbacks
 * don't count as "set up"), the club-scoped team list, and the club user
 * list. Disappears once every step is done, or when dismissed (persisted
 * per club in localStorage).
 */
export function SetupChecklist() {
  const clubQuery = useQuery({
    queryKey: unscopedKey("club-settings"),
    queryFn: getClubSettings,
    retry: false,
  });
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    retry: false,
  });
  const usersQuery = useQuery({
    queryKey: unscopedKey("admin-users"),
    queryFn: getAdminUsers,
    retry: false,
  });

  const clubId = clubQuery.data?.club_id ?? "";
  const dismissKey = `tktic-setup-checklist-dismissed:${clubId}`;
  const [dismissed, setDismissed] = useState(false);

  // Fail closed: no card while loading, on any error, or for non-admins.
  if (!clubQuery.data || !teamsQuery.data || !usersQuery.data) return null;
  if (dismissed || localStorage.getItem(dismissKey) === "1") return null;

  const ownKeys = new Set(clubQuery.data.own_keys ?? []);
  const items: Array<{
    key: string;
    label: string;
    detail: string;
    done: boolean;
    to: string;
  }> = [
    {
      key: "name",
      label: "Set your club's name",
      detail: "The name shown in the app header, browser tab, and emails.",
      done: ownKeys.has("club_title"),
      to: "/admin/settings",
    },
    {
      key: "logo",
      label: "Upload your club logo",
      detail: "Brands the app and your club's emails; the favicon derives from it.",
      done: ownKeys.has("club_logo_filename"),
      to: "/admin/settings",
    },
    {
      key: "team",
      label: "Create your first team",
      detail: "Teams hold rosters, matches, and coaching feedback.",
      done: teamsQuery.data.length > 0,
      to: "/admin/teams",
    },
    {
      key: "people",
      label: "Invite your people",
      detail: "Coaches and team admins join by email invite — rosters never need accounts.",
      done: usersQuery.data.length > 1,
      to: "/admin/people",
    },
  ];

  if (items.every((item) => item.done)) return null;

  const doneCount = items.filter((item) => item.done).length;

  return (
    <section
      data-testid="admin-setup-checklist"
      className="rounded border border-accent/40 bg-surface p-3 shadow-sm"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-ink">
            Finish setting up your club
          </h2>
          <p className="mt-1 text-xs text-ink-muted">
            {doneCount} of {items.length} steps done.
          </p>
        </div>
        <button
          type="button"
          aria-label="Dismiss setup checklist"
          data-testid="admin-setup-checklist-dismiss"
          onClick={() => {
            localStorage.setItem(dismissKey, "1");
            setDismissed(true);
          }}
          className="rounded p-1 text-ink-muted transition-colors hover:bg-field hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </header>
      <ul className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        {items.map((item) => (
          <li key={item.key}>
            <Link
              to={item.to}
              data-testid={`admin-setup-checklist-${item.key}`}
              data-done={item.done ? "true" : undefined}
              className="flex items-start gap-2 rounded border border-rule bg-elevated p-2 transition-colors hover:bg-field focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {item.done ? (
                <CheckCircle2
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-good"
                />
              ) : (
                <Circle
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted"
                />
              )}
              <span className="min-w-0">
                <span
                  className={`block font-display text-[0.65rem] font-semibold uppercase tracking-wider ${
                    item.done ? "text-ink-muted line-through" : "text-ink"
                  }`}
                >
                  {item.label}
                </span>
                <span className="mt-0.5 block text-[0.62rem] leading-4 text-ink-muted">
                  {item.detail}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
